import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Language, StartConversationRequest, StartConversationResponse } from '@shared/types';
import apiService from '../services/api.service';

/**
 * VisitorForm Component
 * 
 * Collects visitor information (name, email) and initiates a conversation.
 * Performs client-side validation before submitting to the backend.
 * 
 * Requirements:
 * - FR-004: Visitor information collection (name, email)
 * - FR-026: Rate limiting (3 conversations per IP per 24h)
 * - US-001: Language selection and visitor registration
 */

interface VisitorFormProps {
  /**
   * Current language selected by the user
   */
  language: Language;
  
  /**
   * Callback when conversation is successfully started
   * @param response - The response from the backend containing conversationId, firstQuestion, etc.
   */
  onConversationStart: (response: StartConversationResponse) => void;
  
  /**
   * Optional CSS classes to apply to the form container
   */
  className?: string;
}

interface FormData {
  name: string;
  email: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  general?: string;
}

export const VisitorForm: React.FC<VisitorFormProps> = ({
  language,
  onConversationStart,
  className = '',
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Client-side validation
   * Returns true if form is valid, false otherwise
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation (min 2 characters)
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = t('visitorForm.errors.nameRequired');
    } else if (trimmedName.length < 2) {
      newErrors.name = t('visitorForm.errors.nameMinLength');
    }

    // Email validation (basic RFC 5322 pattern)
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      newErrors.email = t('visitorForm.errors.emailRequired');
    } else if (!emailPattern.test(trimmedEmail)) {
      newErrors.email = t('visitorForm.errors.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles input field changes
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear general error
    setErrors((prev) => ({ ...prev, general: undefined }));

    // Client-side validation
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: StartConversationRequest = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        language,
      };

      const response = await apiService.post<StartConversationResponse>(
        '/api/conversations',
        requestData
      );

      // Success - trigger callback with response data
      onConversationStart(response.data);
      
    } catch (error: any) {
      console.error('Failed to start conversation:', error);
      
      // Handle specific error types
      if (error.response?.status === 429) {
        // Rate limit exceeded
        setErrors({
          general: t('visitorForm.errors.rateLimitExceeded'),
        });
      } else if (error.response?.status === 400) {
        // Validation error from backend
        const errorMessage = error.response?.data?.message || error.response?.data?.error;
        setErrors({
          general: errorMessage || t('visitorForm.errors.validationFailed'),
        });
      } else if (error.response?.status >= 500) {
        // Server error
        setErrors({
          general: t('visitorForm.errors.serverError'),
        });
      } else {
        // Network or unknown error
        setErrors({
          general: t('visitorForm.errors.networkError'),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${className}`}
      noValidate
      aria-label={t('visitorForm.ariaLabel')}
    >
      {/* Name Field */}
      <div>
        <label
          htmlFor="visitor-name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {t('visitorForm.nameLabel')}
          <span className="text-red-500 ml-1" aria-label={t('common.required')}>
            *
          </span>
        </label>
        <input
          type="text"
          id="visitor-name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder={t('visitorForm.namePlaceholder')}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'name-error' : undefined}
          className={`
            w-full px-4 py-3 rounded-lg border transition-colors
            focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-700
            ${
              errors.name
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600'
            }
          `}
        />
        {errors.name && (
          <p id="name-error" className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.name}
          </p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label
          htmlFor="visitor-email"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {t('visitorForm.emailLabel')}
          <span className="text-red-500 ml-1" aria-label={t('common.required')}>
            *
          </span>
        </label>
        <input
          type="email"
          id="visitor-email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder={t('visitorForm.emailPlaceholder')}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
          className={`
            w-full px-4 py-3 rounded-lg border transition-colors
            focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-700
            ${
              errors.email
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600'
            }
          `}
        />
        {errors.email && (
          <p id="email-error" className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.email}
          </p>
        )}
      </div>

      {/* General Error Message */}
      {errors.general && (
        <div
          className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
          role="alert"
        >
          <p className="text-sm text-red-800 dark:text-red-200">{errors.general}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={`
          w-full px-6 py-3 rounded-lg font-medium text-white
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${
            isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
          }
        `}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {t('visitorForm.submitting')}
          </span>
        ) : (
          t('visitorForm.submitButton')
        )}
      </button>

      {/* Privacy Notice */}
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        {t('visitorForm.privacyNotice')}
      </p>
    </form>
  );
};
